import { useEffect, useRef, useState } from 'react';
import { TongueScene } from './TongueScene';
import { zoneFor, type DragZone } from './dragZone';
import styles from './TongueDrag.module.css';

/** How far a drag (CSS px up) moves the tongue: rest (0) to the /r/ shape (1) and a bit beyond. */
const PX_PER_POSE = 140;
const MAX_POSE = 1.9;
/** The tongue stops this close to the roof (mm): touching it. */
const CONTACT_MM = 0.4;

/**
 * Drag the tongue up (issue #25): the child drags the middle of the tongue toward the roof of the
 * mouth, and the 3D tongue follows in real time from rest toward the bunched /r/ shape (and past
 * it, until it touches the roof). The narrowest point under the roof glows: yellow while there is
 * a gap, red when touching. `onZone` reports where the tongue is as it moves; `onDone` where it was
 * let go.
 */
export function TongueDrag({
  onZone,
  onDone,
  label,
}: {
  onZone?: (zone: DragZone, gapMm: number) => void;
  onDone?: (zone: DragZone, gapMm: number) => void;
  label: string;
}) {
  const host = useRef<HTMLDivElement>(null);
  const scene = useRef<TongueScene | null>(null);
  const [handle, setHandle] = useState<{ x: number; y: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const pose = useRef(0);
  const start = useRef<{ y: number; pose: number } | null>(null);

  useEffect(() => {
    if (!host.current) return;
    const s = new TongueScene(host.current, { orbitControls: false });
    scene.current = s;
    s.setBlend('rest', 'rBunched', 0);
    s.setGapMarker(true);
    let raf = 0;
    // The handle follows the middle of the tongue on screen.
    const follow = () => {
      const dorsum = s.current.tongue.dorsum;
      const mid = dorsum[Math.floor(dorsum.length * 0.45)];
      if (mid) setHandle(s.toScreen(mid[0], mid[1]));
      raf = requestAnimationFrame(follow);
    };
    void s.ready.then(() => {
      raf = requestAnimationFrame(follow);
    });
    return () => {
      cancelAnimationFrame(raf);
      s.dispose();
    };
  }, []);

  const move = (clientY: number) => {
    const s = scene.current;
    if (!s || !start.current) return;
    let next = Math.min(
      MAX_POSE,
      Math.max(0, start.current.pose + (start.current.y - clientY) / PX_PER_POSE),
    );
    // The tongue stops where it meets the roof (it presses against it, not through it): the
    // farthest pose with the gap at CONTACT_MM, by bisection.
    s.setBlend('rest', 'rBunched', next);
    if (s.gap().mm < CONTACT_MM) {
      let lo = Math.min(next, pose.current);
      let hi = next;
      for (let i = 0; i < 10; i++) {
        const mid = (lo + hi) / 2;
        s.setBlend('rest', 'rBunched', mid);
        if (s.gap().mm < CONTACT_MM) hi = mid;
        else lo = mid;
      }
      next = lo;
      s.setBlend('rest', 'rBunched', next);
    }
    pose.current = next;
    const gap = s.gap().mm;
    onZone?.(zoneFor(gap, next), gap);
  };

  return (
    <div className={styles.stage}>
      <div ref={host} className={styles.model} role="img" aria-label={label} />
      {handle && (
        <button
          type="button"
          className={styles.handle}
          data-dragging={dragging || undefined}
          style={{ transform: `translate(${String(handle.x)}px, ${String(handle.y)}px)` }}
          aria-label="Move the middle of the tongue up or down"
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            start.current = { y: e.clientY, pose: pose.current };
            setDragging(true);
          }}
          onPointerMove={(e) => {
            move(e.clientY);
          }}
          onPointerUp={() => {
            start.current = null;
            setDragging(false);
            const gap = scene.current?.gap().mm ?? Infinity;
            onDone?.(zoneFor(gap, pose.current), gap);
          }}
          onKeyDown={(e) => {
            // Keyboard: arrow keys move the tongue in steps.
            const step = e.key === 'ArrowUp' ? 0.08 : e.key === 'ArrowDown' ? -0.08 : 0;
            if (!step) return;
            e.preventDefault();
            start.current = { y: 0, pose: pose.current };
            move(-step * PX_PER_POSE);
            start.current = null;
            const gap = scene.current?.gap().mm ?? Infinity;
            onDone?.(zoneFor(gap, pose.current), gap);
          }}
        >
          <span aria-hidden>↕</span>
        </button>
      )}
    </div>
  );
}
